import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { IAppState } from './app-state';
import { BootstrapAppAction, InitAppAction, RefreshCurrencyLocale } from './app-actions';
import { APP_BOOTSTRAP, APP_INIT, DATETIME_FORMAT_FETCH, GLOBAL_FETCH, GLOBAL_SETTINGS_FETCH, ROUTER_NAVIGATION, SESSION_INIT_COMPLETE, SESSION_MOMENT_LOCALE_UPDATE } from '../action-types';
import { concatMap, from, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { isAuthenticated } from '../auth/auth-selectors';
import { getRouterState } from '../router/router-selectors';
import { IAppRouterState } from '../router/router-state';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { STORE_LOGIN_PREFS, STORE_USER_AUTH } from '@core/local-storage/local-storage';
import { SetUserAuthAction, SetCompanyAuthAction } from '../auth/auth-actions';
import { AuthSession } from '@core/models/auth/auth-session';
import { LIVEVIEW, LOGIN, NO_STATE, REGISTRATION } from '@core/router/app-states';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { FetchCountryAction, FetchUserAction, InitSessionAction, InitSessionCompleteAction } from '../session/session-actions';
import { getSessionLocale } from '../session/session-selectors';
import { LanguageService } from '@core/translation/language.service';
import { environment } from '@environments/environment';
import { FetchDatetimeFormatAction, FetchGlobalAction, FetchSettingsAction, SetDatetimeFormatAction, SetGlobalAction, SetSettingsAction, UpdateMomentLocaleAction } from '../global/global-actions';
import { GlobalsDataService } from '@core/data/api/global/global-data.service';
import * as moment from 'moment';
import { RouterGoAction } from '../router/router-actions';
import { NoAction } from '../shared/actions/no-action';

@Injectable()
export class AppEffects {

    constructor(private actions$: Actions,
                private store$: Store<IAppState>,
                private globalsDataService: GlobalsDataService,
                private languageService: LanguageService,
                private localStorage: LocalStorageService) {}
    /**
     * Init the application action.
     */
    appInit$ = createEffect(() => this.actions$
      .pipe(
        ofType<InitAppAction>(APP_INIT),
        withLatestFrom(
          this.store$.select(getSessionLocale),
          this.store$.select(isAuthenticated),
          this.store$.select(getRouterState)
        ),
        switchMap(([action, locale, isAuthenticated, routerState]: [InitAppAction, string, boolean, IAppRouterState]) => {
            const actions = [];
            const authorisationSet = this.localStorage.getItem(STORE_USER_AUTH);

            // Fetch app globals actions
            actions.push(new FetchGlobalAction());
            actions.push(new FetchSettingsAction());
            actions.push(new FetchDatetimeFormatAction());
            actions.push(new FetchCountryAction());

            const userAuthSession: AuthSession = authorisationSet?.user;
            const companyAuthSession: AuthSession = authorisationSet?.company;

            if (userAuthSession) {
                actions.push(new SetUserAuthAction(userAuthSession));
            }

            if (companyAuthSession) {
                actions.push(new SetCompanyAuthAction(companyAuthSession));
            }

            // Restore previous application locale
            // userLocale = sessionUser.locale || prevLocale || 'en-US';
            const sessionLocale: string = locale || environment.settings.defaultLocale;
            this.languageService.setLocale(sessionLocale);

            if (!routerState?.name || routerState.name === LOGIN || routerState.name.indexOf(REGISTRATION) > -1 || routerState.name === NO_STATE) {
                if (companyAuthSession?.access_token?.length) {
    

                    // Manager or Admin is authenticated

                    actions.push(new InitSessionAction());

                    // Existing authentication found so fetch kiosk session
                    return from(actions);

                } else if (userAuthSession?.access_token?.length) {

                    // Dev support user is authenticated
                    actions.push(new FetchUserAction());

                    return from(actions);
                    
                } else {

                    const loginPrefs = this.localStorage.getItem(STORE_LOGIN_PREFS);
                    const params = { mode: loginPrefs === 'email' ? 'email' : null };

                    return from(actions);
                }
            } else {
                actions.push(new InitSessionAction());
                return actions;
            }
        })
      )
    );

    appInitComplete$ = createEffect(() => this.actions$
        .pipe(
            ofType<InitSessionCompleteAction>(SESSION_INIT_COMPLETE),
            map((action) => action.payload.isLogin && !action.payload.bypassRedirection ? new RouterGoAction({ name: LIVEVIEW, options: { reload: true } }) : new NoAction()),
            ossCatchError((errorActions) => from(errorActions))
        ));

    /**
     * Init the application action.
     */
    appBootstrap$ = createEffect(() => this.actions$
      .pipe(
        ofType<BootstrapAppAction>(APP_BOOTSTRAP),
        concatMap((_) => {
            return [
            ];
        }),
        ossCatchError((errorActions) => from(errorActions))
    ));

    /**
     * Fetch the application globals.
     */
    fetchGlobals$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchGlobalAction>(GLOBAL_FETCH),
            switchMap(() => {
                // Restore previous application locale
                    return this.globalsDataService.fetchGlobals()
                    .pipe(
                        mergeMap((response) => {

                            return [
                                new SetGlobalAction(response),
                                new RefreshCurrencyLocale(),
                                new UpdateMomentLocaleAction(),
                            ];
                        }),
                        ossCatchError(([errorActions, errorResponse]) => from(errorActions))
            
                
                    )
            })
                    
        )
    );

    fetchSettings$ = createEffect(() =>
        this.actions$
        .pipe(
            ofType<FetchSettingsAction>(GLOBAL_SETTINGS_FETCH),
            switchMap(() => 
                
                this.globalsDataService.fetchSettings().pipe(
                    mergeMap((response) => {
                        return [new SetSettingsAction(response)]
                    }),
                    ossCatchError(([errorActions, errorResponse]) => from(errorActions))
                    )
            )
        )
    );

    /**
     * Fetch the application formats
     */
    initDatetimeFormat$ = createEffect(() => 
        this.actions$
            .pipe(
                ofType<FetchDatetimeFormatAction>(DATETIME_FORMAT_FETCH),
                switchMap(() =>
                    this.globalsDataService.fetchDatetimeFormats()
                        .pipe(
                            mergeMap((response) => {
                                return [
                                    new SetDatetimeFormatAction(response)
                                ];
                            }),
                            ossCatchError(([errorActions, errorResponse]) =>  from(errorActions))
                        )
                )
            )
    );

    updateMomentLocale$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType<UpdateMomentLocaleAction>(SESSION_MOMENT_LOCALE_UPDATE),
                tap(() => {
                    
                    moment.updateLocale('en-us', {
                        ordinal : function (number) {
                            var b = number % 10;
                            var output = (~~ (number % 100 / 10) === 1) ? 'th' :
                                (b === 1) ? 'st' :
                                (b === 2) ? 'nd' :
                                (b === 3) ? 'rd' : 'th';
                            return number + output;
                        }
                    });
                })
            )
    }, { dispatch: false });


    routeTracking$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        withLatestFrom(
          this.store$.select(getRouterState)
        ),
        mergeMap(([_action, routerState]: [Action, IAppRouterState]) => {
          const actions: Action[] = [];
  
          return actions;
        })
    )}
  );
}